import { observer } from "mobx-react-lite";
import { useRef, useState } from "react";
import { Button, Container, Message } from "semantic-ui-react";
import { ImportsApi } from "../../../app/api/ImportsApi";
import { useTranslation } from "react-i18next";
import PageHeader from "../../../app/layout/PageHeader";

interface FileChangeEvent extends React.ChangeEvent<HTMLInputElement> {
  target: HTMLInputElement & EventTarget & { files: FileList };
}

export default observer(function ImportDisclaimers() {
  const [file, setFile] = useState<File | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>();
  const { t } = useTranslation("imports");

  const handleFileChange = (e: FileChangeEvent) => {
    setFile(e.target.files[0]);
  };

  const uploadFile = async () => {
    if (!file) return;

    setLoading(true);

    try {
      setSuccess(false);
      setError(undefined);

      const id = await ImportsApi.getUploadId();

      const url = await ImportsApi.getUploadUrl(`${id}.mdb`);

      await ImportsApi.uploadFile(url, file);
      await ImportsApi.addImportJob(id.toString());

      setFile(undefined);
      setSuccess(true);
    } catch (error) {
      console.log(error);
      setError((error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  return (
    <Container className="page">
      <PageHeader
        header={t("uploadBackup")}
        type={"h3"}
        divider={false}
        getAlerts={false}
        addTitle={false}
      />

      {success && <Message positive>{t("uploads.success")}</Message>}
      {error && <Message error>{`${t("uploads.error")}: ${error}`}</Message>}
      <Button
        content={t("chooseFile")}
        labelPosition="left"
        icon="file"
        onClick={() => fileInputRef?.current?.click()}
        disabled={loading}
      />
      <input
        type="file"
        hidden
        onChange={handleFileChange}
        accept="*.bak,application/x-msaccess"
        ref={fileInputRef}
      />
      <Button
        color="green"
        content={t("startUpload")}
        labelPosition="left"
        icon="upload"
        loading={loading}
        disabled={loading || !file}
        onClick={uploadFile}
      />
      <div>{file ? file.name : null}</div>
    </Container>
  );
});
